/**
 * The settings catalog: tabs, sections, labels, sentences, server defaults
 * and option words. Data only; the logic that reads it is settings-meta.ts.
 */

/** The unit of an amount in minor units; settingUnit() writes it out for the cell's currency. */
export const MINOR_UNITS = 'minor units';

/** The tabs the settings are spread over. */
export const SETTING_TABS = [
  { key: 'channels', label: 'Money channels' },
  { key: 'money', label: 'Money and limits' },
  { key: 'approvals', label: 'Approvals and staff' },
  { key: 'customers', label: 'Customers' },
  { key: 'pilot', label: 'Pilot' },
  { key: 'data', label: 'Data' },
  { key: 'other', label: 'Other' },
] as const;

export type SettingTab = (typeof SETTING_TABS)[number]['key'];

/** A settings section: its title, one plain line, and the tab it sits on. */
export interface SettingGroup {
  id: string;
  title: string;
  blurb: string;
  tab: SettingTab;
}

export interface SettingMeta {
  label: string;
  group: string;
  unit?: string;
}

export const GROUPS: readonly SettingGroup[] = [
  {
    id: 'pilot',
    title: 'Pilot',
    blurb:
      'Show a feature to a few people first. Put the feature in Pilot mode (its flag key under Features in Pilot mode) and add the people. They see it fully on; everyone else is unchanged. Turn the flag on under Feature flags when it is for everyone.',
    tab: 'pilot',
  },
  {
    id: 'topups',
    title: 'Adding money',
    blurb: 'Where each mobile network collects money from customers.',
    tab: 'channels',
  },
  {
    id: 'payouts',
    title: 'Sending money out',
    blurb: 'How each mobile network is paid, and the code used for Access GIP.',
    tab: 'channels',
  },
  {
    id: 'money',
    title: 'Pool and charges',
    blurb: 'Checks on the money pool and its charge limit.',
    tab: 'money',
  },
  {
    id: 'reversals',
    title: 'Reversals',
    blurb: 'When and how a payment can be taken back.',
    tab: 'money',
  },
  {
    id: 'sweeps',
    title: 'Sweeps to the pool',
    blurb: 'Moving what Hubtel owes into the Access pool.',
    tab: 'money',
  },
  {
    id: 'approvals',
    title: 'Approvals',
    blurb: 'How long a request waits and when a second checker is needed.',
    tab: 'approvals',
  },
  {
    id: 'session',
    title: 'Staff sign-in',
    blurb: 'When staff are signed out.',
    tab: 'approvals',
  },
  {
    id: 'exports',
    title: 'Exports',
    blurb: 'Limits on the files staff download.',
    tab: 'approvals',
  },
  {
    id: 'devices',
    title: 'Devices',
    blurb: 'How many phones a customer can use.',
    tab: 'customers',
  },
  {
    id: 'referrals',
    title: 'Referrals',
    blurb: 'What counts as a referral and what it earns.',
    tab: 'customers',
  },
  {
    id: 'issues',
    title: 'Issues',
    blurb: 'Customer issue reports and when they are due.',
    tab: 'customers',
  },
  {
    id: 'sms',
    title: 'SMS and sign-in codes',
    blurb: 'Who sends the codes customers receive.',
    tab: 'customers',
  },
  {
    id: 'retention',
    title: 'Keeping records',
    blurb: 'How long records are kept before they are removed.',
    tab: 'data',
  },
  {
    id: 'other',
    title: 'Other',
    blurb: 'Settings without a section yet.',
    tab: 'other',
  },
];

/** One plain sentence for each setting. */
export const HELP: Record<string, string> = {
  'topups.route_mtn':
    'Where MTN customers add money from: Access OVA, Hubtel or off.',
  'topups.route_telecel':
    'Where Telecel customers add money from: Access OVA, Hubtel or off.',
  'topups.route_at':
    'Where AirtelTigo customers add money from: Hubtel or off.',
  'payouts.route_mtn':
    'How money is sent to MTN wallets: Telco direct, Access GIP or off.',
  'payouts.route_telecel':
    'How money is sent to Telecel wallets: Telco direct, Access GIP or off.',
  'payouts.route_at':
    'How money is sent to AirtelTigo wallets: Access GIP or off.',
  'payouts.bank_channel':
    'How money is sent to bank accounts. Access GIP carries every bank by default; Hubtel Send-To-Bank is a second channel for testing; Off stops sending to banks.',
  'payouts.gip_code_mtn':
    "MTN's bank code, used when MTN is sent by Access GIP.",
  'payouts.gip_code_telecel':
    "Telecel's bank code, used when Telecel is sent by Access GIP.",
  'payouts.gip_code_at':
    "AirtelTigo's bank code, used when AirtelTigo is sent by Access GIP.",
  'referrals.milestone':
    'What a new customer must do for the referral to count.',
  'referrals.min_topup_minor':
    'The smallest top-up that makes a referral count.',
  'referrals.qualify_within_days':
    'How long a new customer has to complete the milestone.',
  'referrals.max_per_user':
    'The most successful referrals one person can have.',
  'referrals.max_pending_per_user':
    'The most referrals one person can have waiting at once.',
  'referrals.block_shared_device':
    'Refuse a referral when both people use the same phone.',
  'referrals.reward_kind': 'What a referral earns.',
  'referrals.reward_referrer_minor': 'What the person who referred earns.',
  'referrals.reward_referred_minor':
    'What the new customer earns as a welcome.',
  'referrals.bonus_every_n': 'Give a bonus at every Nth successful referral.',
  'referrals.bonus_minor': 'The size of that bonus.',
  'referrals.reward_cap_per_user_minor':
    'The most one person can earn from referrals in total.',
  'referrals.monthly_budget_minor':
    'The most the programme pays out in a month.',
  'referrals.rewards_need_approval':
    'A checker must approve each reward before it is paid.',
  'change_requests.expiry_hours':
    'How long a change request waits for approval before it lapses.',
  'money.second_checker_above_minor':
    'Changes above this amount need a second checker.',
  'devices.max_bound': 'How many phones one customer can sign in on.',
  'exports.link_minutes': 'How long a download link works.',
  'exports.max_rows': 'The most rows one export can hold.',
  'issues.due_hours': 'How long staff have to answer a customer issue.',
  'money.pool_checks': 'Whether the daily check of the money pool runs.',
  'checks.books_gap_tolerance_minor':
    'How far the bank may differ from the books before staff are alerted.',
  'checks.unexplained_hold_line_minor':
    'An unexplained debit on the pool larger than this holds every payout. Smaller ones alert staff only.',
  'checks.unexplained_hold_total_minor':
    'When the open unexplained debits on the pool add up to more than this, every payout is held.',
  'pool.charge_cap_minor':
    'The most the bank may charge in one go before staff are alerted.',
  'partners.payer_required_minor':
    'A partner Cash-in at or above this must say who is paying. Zero means never.',
  'partners.pool_credit_max_minor':
    'The most one pool credit from a partner may be. Zero means no ceiling.',
  'pool.cover_floor_minor':
    "Staff are alerted when Sao's own money in the pool, beyond what customers are owed, falls under this.",
  'retention.closed_accounts_days': 'How long a closed account is kept.',
  'retention.exports_days': 'How long export files are kept.',
  'retention.provider_payloads_days':
    'How long the raw replies from payment partners are kept.',
  'reversals.hold_receiver':
    "Hold the receiver's money while a reversal is being looked at.",
  'reversals.receiver_consent':
    'Ask the receiver before a payment is reversed.',
  'reversals.window_hours':
    'How long after a payment it can still be reversed.',
  'session.idle_minutes': 'Sign staff out after this long without activity.',
  'session.wall_max_hours': 'How long the wall screens stay signed in.',
  'session.max_hours': 'The longest a staff session can last.',
  'sweeps.switch': 'Whether sweeps to the Access pool run.',
  'sweeps.auto':
    'Whether the system asks for sweeps itself, inside the sweep policy. Needs the sweeps switch on too.',
  'sweeps.bills_reserve_minor': 'Money kept at Hubtel to pay bills.',
  'sweeps.floor_minor': 'The least cash left in the Access pool after a sweep.',
  'sweeps.owed_alert_minor': 'Alert when Hubtel owes more than this.',
  'sweeps.owed_age_hours': 'Alert when money has been owed for this long.',
  'pilot.users':
    'Phone numbers or user ids of the people who see the features in Pilot mode. Empty: nobody.',
  'pilot.features':
    'Flag keys in Pilot mode, such as card_funding (its parts, such as card_funding_sdk, come with it). Fully on for the pilot people, unchanged for everyone else. Empty: none.',
  'notices.sms_money':
    'Whether money received and money sent are also texted. Each text is billed.',
  'sms.gti_sender_id': 'The name customers see on GTI messages.',
  'sms.gti_test_numbers': 'Phone numbers that always get the test code.',
  'sms.hubtel_sender': 'The name customers see on Hubtel messages.',
  'sms.provider': 'Which company sends the codes.',
  'sms.zavu_sender': 'The name customers see on Zavu messages.',
};

/**
 * What the server does while a setting has never been set, as people read it.
 * The server sends no value for such a setting; these come from its own rules
 * (each key's description in admin.setting_keys).
 */
export const DEFAULTS: Record<string, string> = {
  'topups.route_mtn': 'Access OVA',
  'topups.route_telecel': 'Off',
  'topups.route_at': 'Off',
  'payouts.route_mtn': 'Telco direct',
  'payouts.route_telecel': 'Telco direct',
  'payouts.route_at': 'Off',
  'payouts.bank_channel': 'Access GIP',
  'app.min_store_version': 'Every version',
  'attestation.android_mode': 'Log',
  'attestation.ios_mode': 'Log',
  'attestation.min_app_version': '0.0.5',
  'devices.max_bound': '2',
  'devices.verification': 'Off',
  'referrals.block_shared_device': 'Yes',
  'referrals.max_pending_per_user': 'No limit',
  'referrals.max_per_user': '50',
  'referrals.milestone': 'ID approved',
  'referrals.min_topup_minor': 'Any top-up',
  'referrals.qualify_within_days': 'No limit',
  'referrals.reward_kind': 'No reward',
  'referrals.rewards_need_approval': 'No',
  'retention.closed_accounts_days': 'Kept forever',
  'retention.exports_days': 'Kept forever',
  'retention.provider_payloads_days': 'Kept forever',
  'reversals.hold_receiver': 'No',
  'reversals.receiver_consent': 'No',
  'reversals.window_hours': 'No limit',
  'notices.sms_money': 'Yes',
  'sms.hubtel_sender': 'sao',
  'sweeps.floor_minor': 'No floor',
  'sweeps.owed_alert_minor': 'No alert',
};

/** Route options shown but not offered yet. */
export const SOON: Record<string, readonly string[]> = {
  'payouts.route_mtn': ['Sao direct'],
  'payouts.route_telecel': ['Sao direct'],
  'payouts.route_at': ['Sao direct'],
  'payouts.bank_channel': ['Sao GIP'],
};

export const META: Record<string, SettingMeta> = {
  'topups.route_mtn': { label: 'MTN add money', group: 'topups' },
  'topups.route_telecel': { label: 'Telecel add money', group: 'topups' },
  'topups.route_at': { label: 'AirtelTigo add money', group: 'topups' },
  'payouts.route_mtn': { label: 'MTN sending', group: 'payouts' },
  'payouts.route_telecel': { label: 'Telecel sending', group: 'payouts' },
  'payouts.route_at': { label: 'AirtelTigo sending', group: 'payouts' },
  'payouts.bank_channel': { label: 'Wallet to bank', group: 'payouts' },
  'payouts.gip_code_mtn': {
    label: 'MTN Access GIP code',
    group: 'payouts',
  },
  'payouts.gip_code_telecel': {
    label: 'Telecel Access GIP code',
    group: 'payouts',
  },
  'payouts.gip_code_at': {
    label: 'AirtelTigo Access GIP code',
    group: 'payouts',
  },
  'referrals.milestone': {
    label: 'What makes a referral successful',
    group: 'referrals',
  },
  'referrals.min_topup_minor': {
    label: 'Smallest top-up that counts',
    group: 'referrals',
    unit: MINOR_UNITS,
  },
  'referrals.qualify_within_days': {
    label: 'Time to become successful',
    group: 'referrals',
    unit: 'days after signing up',
  },
  'referrals.max_per_user': {
    label: 'Successful referrals per person',
    group: 'referrals',
    unit: 'referrals',
  },
  'referrals.max_pending_per_user': {
    label: 'Waiting referrals per person',
    group: 'referrals',
    unit: 'referrals',
  },
  'referrals.block_shared_device': {
    label: 'Refuse the same phone',
    group: 'referrals',
  },
  'referrals.reward_kind': { label: 'Reward type', group: 'referrals' },
  'referrals.reward_referrer_minor': {
    label: 'Reward to the referrer',
    group: 'referrals',
    unit: MINOR_UNITS,
  },
  'referrals.reward_referred_minor': {
    label: 'Welcome reward to the new customer',
    group: 'referrals',
    unit: MINOR_UNITS,
  },
  'referrals.bonus_every_n': {
    label: 'Bonus at every Nth referral',
    group: 'referrals',
    unit: 'referrals',
  },
  'referrals.bonus_minor': {
    label: 'Bonus amount',
    group: 'referrals',
    unit: MINOR_UNITS,
  },
  'referrals.reward_cap_per_user_minor': {
    label: 'Most rewards one person can earn',
    group: 'referrals',
    unit: MINOR_UNITS,
  },
  'referrals.monthly_budget_minor': {
    label: 'Programme budget per month',
    group: 'referrals',
    unit: MINOR_UNITS,
  },
  'referrals.rewards_need_approval': {
    label: 'A checker approves rewards',
    group: 'referrals',
  },
  'change_requests.expiry_hours': {
    label: 'Change request expiry',
    group: 'approvals',
    unit: 'hours',
  },
  'money.second_checker_above_minor': {
    label: 'Second checker above',
    group: 'approvals',
    unit: 'pesewas',
  },
  'devices.max_bound': { label: 'Devices per customer', group: 'devices' },
  'exports.link_minutes': {
    label: 'Download link lifetime',
    group: 'exports',
    unit: 'minutes',
  },
  'exports.max_rows': {
    label: 'Rows per export',
    group: 'exports',
    unit: 'rows',
  },
  'issues.due_hours': {
    label: 'Issue due after',
    group: 'issues',
    unit: 'hours',
  },
  'money.pool_checks': { label: 'Pool checks', group: 'money' },
  'checks.books_gap_tolerance_minor': {
    label: 'Books vs bank tolerance',
    group: 'money',
    unit: 'pesewas',
  },
  'checks.unexplained_hold_line_minor': {
    label: 'Unexplained debit hold (one line)',
    group: 'money',
    unit: 'pesewas',
  },
  'checks.unexplained_hold_total_minor': {
    label: 'Unexplained debit hold (all open)',
    group: 'money',
    unit: 'pesewas',
  },
  'pool.charge_cap_minor': {
    label: 'Pool charge cap',
    group: 'money',
    unit: 'pesewas',
  },
  'partners.payer_required_minor': {
    label: 'Payer details from',
    group: 'money',
    unit: 'pesewas',
  },
  'partners.pool_credit_max_minor': {
    label: 'Largest pool credit',
    group: 'money',
    unit: 'pesewas',
  },
  'pool.cover_floor_minor': {
    label: 'Cover floor',
    group: 'money',
    unit: 'pesewas',
  },
  'retention.closed_accounts_days': {
    label: 'Keep closed accounts',
    group: 'retention',
    unit: 'days',
  },
  'retention.exports_days': {
    label: 'Keep export files',
    group: 'retention',
    unit: 'days',
  },
  'retention.provider_payloads_days': {
    label: 'Keep provider payloads',
    group: 'retention',
    unit: 'days',
  },
  'reversals.hold_receiver': { label: 'Hold the receiver', group: 'reversals' },
  'reversals.receiver_consent': {
    label: 'Receiver consent',
    group: 'reversals',
  },
  'reversals.window_hours': {
    label: 'Reversal window',
    group: 'reversals',
    unit: 'hours',
  },
  'session.idle_minutes': {
    label: 'Sign out when idle',
    group: 'session',
    unit: 'minutes',
  },
  'session.wall_max_hours': {
    label: 'Wall screens stay signed in for',
    group: 'session',
    unit: 'hours',
  },
  'session.max_hours': {
    label: 'Longest session',
    group: 'session',
    unit: 'hours',
  },
  'sweeps.switch': { label: 'Sweeps switch', group: 'sweeps' },
  'sweeps.auto': { label: 'Automatic sweeps', group: 'sweeps' },
  'sweeps.bills_reserve_minor': {
    label: 'Kept at Hubtel for bills',
    group: 'sweeps',
    unit: MINOR_UNITS,
  },
  'sweeps.floor_minor': {
    label: 'Access cash floor',
    group: 'sweeps',
    unit: MINOR_UNITS,
  },
  'sweeps.owed_alert_minor': {
    label: 'Alert when Hubtel owes more than',
    group: 'sweeps',
    unit: MINOR_UNITS,
  },
  'sweeps.owed_age_hours': {
    label: 'Alert when money is owed for longer than',
    group: 'sweeps',
    unit: 'hours',
  },
  'pilot.users': { label: 'People in the pilot', group: 'pilot' },
  'pilot.features': { label: 'Features in Pilot mode', group: 'pilot' },
  'notices.sms_money': { label: 'Text money notices', group: 'sms' },
  'sms.gti_sender_id': { label: 'GTI sender name', group: 'sms' },
  'sms.gti_test_numbers': { label: 'GTI test numbers', group: 'sms' },
  'sms.hubtel_sender': { label: 'Hubtel sender name', group: 'sms' },
  'sms.provider': { label: 'SMS provider', group: 'sms' },
  'sms.zavu_sender': { label: 'Zavu sender', group: 'sms' },
};

/** Words for the raw values a text setting allows, where the raw value reads badly. */
const ROUTE_TEXT = {
  access: 'Access OVA',
  hubtel: 'Hubtel',
  direct: 'Telco direct',
  gip: 'Access GIP',
  off: 'Off',
};

const ON_OFF = { on: 'On', off: 'Off' };
const ATTESTATION_TEXT = { off: 'Off', log: 'Log', enforce: 'Enforce' };

export const OPTION_TEXT: Record<string, Record<string, string>> = {
  'topups.route_mtn': ROUTE_TEXT,
  'topups.route_telecel': ROUTE_TEXT,
  'topups.route_at': ROUTE_TEXT,
  'payouts.route_mtn': ROUTE_TEXT,
  'payouts.route_telecel': ROUTE_TEXT,
  'payouts.route_at': ROUTE_TEXT,
  'payouts.bank_channel': {
    access_gip: 'Access GIP',
    hubtel_bank: 'Hubtel Send-To-Bank',
    off: 'Off',
  },
  'referrals.milestone': {
    kyc_approved: 'ID approved',
    first_topup: 'First top-up',
    topup_amount: 'Top-up of at least an amount',
  },
  'referrals.reward_kind': {
    none: 'No reward',
    wallet_credit: 'Wallet credit',
    points: 'Points',
  },
  'sweeps.switch': ON_OFF,
  'sweeps.auto': ON_OFF,
  'devices.verification': { off: 'Off', enforce: 'On' },
  'attestation.android_mode': ATTESTATION_TEXT,
  'attestation.ios_mode': ATTESTATION_TEXT,
  'sms.provider': { hubtel: 'Hubtel', zavu: 'Zavu', gti: 'GTI' },
};

export const SUFFIX_UNITS: Record<string, string> = {
  _hours: 'hours',
  _minutes: 'minutes',
  _days: 'days',
  _minor: 'pesewas',
};
